import { useCallback, useEffect, useMemo } from 'react';
import isEqual from 'react-fast-compare';
import { useForm } from 'react-hook-form';
import { useAutoPricingPreview } from 'src/components/Listings/MapWithCompListingsPreview/MapWithCompListingsPreview.hooks';
import { useActivePosEntityContext } from 'src/contexts/ActivePosEntityContext';
import { useAppContext } from 'src/contexts/AppContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { useEventMapContext } from 'src/contexts/EventMapContext';
import { useGetListingAutoPricingSettings } from 'src/hooks/useGetListingAutoPricingSettings';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { isSuccess } from 'src/utils/errorUtils';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import { mapToSelectedSectionSettings } from 'src/utils/types/selectedSectionSettingsWithOptionals';
import {
  Feature,
  Listing,
  ListingClient,
  ListingDetails,
  ListingDetailsAutoPricingSectionUpdates,
} from 'src/WebApiController';

import { buildListingPricingPreviewInput } from './buildListingPricingPreviewInput';

const ensureZoneMapFlagConsistency = (
  settings: ListingDetailsAutoPricingSectionUpdates | null | undefined
): ListingDetailsAutoPricingSectionUpdates => {
  const result = {
    ...(settings ?? ({} as ListingDetailsAutoPricingSectionUpdates)),
  };
  if (result.compListingSelectedSectionSettings?.sectionIdFilter?.length) {
    result.compListingOnlyForSelectedSectionsEnabled = true;
    result.compListingSelectedSectionSettings = mapToSelectedSectionSettings(
      result.compListingSelectedSectionSettings
    )!;
    result.compListingSelectedSectionSettings.sectionIdFilterFromZoneMap = false;
  }
  return result;
};

export const useAutoPricingForm = (
  listing: Listing | null | undefined,
  onSubmitStart: () => void,
  onSubmitDone: (success?: boolean) => Promise<void>
) => {
  const { activeAccountWebClientConfig } = useAppContext();
  const { showErrorDialog } = useClientLogContext();
  const { event } = useActivePosEntityContext<ListingDetails>();
  const {
    pricingSettings: listingPricingSettings,
    loaded: listingSettingLoaded,
  } = useGetListingAutoPricingSettings(listing, event);

  const hasSetFloorToPercentOfMarketPriceDefaultFeature = useUserHasFeature(
    Feature.SetFloorToPercentOfMarketPriceDefault
  );

  const methods = useForm<ListingDetailsAutoPricingSectionUpdates>({
    defaultValues: {},
  });

  useEffect(() => {
    if (listingSettingLoaded) {
      methods.reset(ensureZoneMapFlagConsistency(listingPricingSettings));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listingPricingSettings, listingSettingLoaded]);

  const input = methods.watch();

  const { event: mapEvent } = useEventMapContext();
  const sanitizedListingInput = useMemo(
    () => buildListingPricingPreviewInput(input, listing?.id),
    [listing?.id, input]
  );

  const { priceCalcQuery } = useAutoPricingPreview(
    mapEvent?.viagId ?? null,
    listing?.id,
    sanitizedListingInput
  );
  const modalMarketPriceAllIn = priceCalcQuery?.data?.websitePrice?.amt;

  const onSubmitHandler = useCallback(
    async (autoPricingSectionForm: ListingDetailsAutoPricingSectionUpdates) => {
      tryInvokeApi(
        async () => {
          onSubmitStart();
          const payload: ListingDetailsAutoPricingSectionUpdates =
            hasSetFloorToPercentOfMarketPriceDefaultFeature &&
            listing?.id != null &&
            modalMarketPriceAllIn != null
              ? {
                  ...autoPricingSectionForm,
                  previewMarketPrices: {
                    [listing.id]: modalMarketPriceAllIn,
                  },
                }
              : autoPricingSectionForm;
          const result = await new ListingClient(
            activeAccountWebClientConfig
          ).updateListingAutoPricingSectionByListingId(payload.id, payload);

          if (isSuccess(result)) {
            // Reset the form to latest changed
            methods.reset(autoPricingSectionForm);
            await onSubmitDone();
          } else {
            showErrorDialog(
              'ListingClient.updateListingAutoPricingSection',
              { message: result.message, status: result.status },
              {
                trackErrorData: {
                  autoPricingSectionForm,
                },
              }
            );
          }
        },
        (error) => {
          showErrorDialog(
            'ListingClient.updateListingAutoPricingSection',
            error,
            {
              trackErrorData: autoPricingSectionForm,
            }
          );
        },
        () => {
          onSubmitDone(false);
        }
      );
    },
    [
      activeAccountWebClientConfig,
      hasSetFloorToPercentOfMarketPriceDefaultFeature,
      listing?.id,
      methods,
      modalMarketPriceAllIn,
      onSubmitDone,
      onSubmitStart,
      showErrorDialog,
    ]
  );

  const onSubmit = useCallback(() => {
    methods.handleSubmit(onSubmitHandler)();
  }, [methods, onSubmitHandler]);

  const onReset = useCallback(() => {
    methods.reset(ensureZoneMapFlagConsistency(listingPricingSettings));
  }, [listingPricingSettings, methods]);

  const hasChanges = useMemo(() => {
    const defaultValues = methods.formState.defaultValues;

    return (
      input.netProceedsFloor !== defaultValues?.netProceedsFloor ||
      input.netProceedsCeiling !== defaultValues?.netProceedsCeiling ||
      input.currencyCode !== defaultValues?.currencyCode ||
      input.autoPricingEnabled !== defaultValues?.autoPricingEnabled ||
      input.compListingFloor !== defaultValues?.compListingFloor ||
      input.compListingCeiling !== defaultValues?.compListingCeiling ||
      input.compListingMode !== defaultValues?.compListingMode ||
      input.compListingOnlyForSameZoneEnabled !==
        defaultValues?.compListingOnlyForSameZoneEnabled ||
      input.compListingOnlyForSelectedSectionsEnabled !==
        defaultValues?.compListingOnlyForSelectedSectionsEnabled ||
      input.compListingExcludeFanInventory !==
        defaultValues?.compListingExcludeFanInventory ||
      input.compListingExcludeDefects !==
        defaultValues?.compListingExcludeDefects ||
      input.compListingIncludeUnmappedInventory !==
        defaultValues?.compListingIncludeUnmappedInventory ||
      input.compListingQuantityScoreAdjustmentEnabled !==
        defaultValues?.compListingQuantityScoreAdjustmentEnabled ||
      input.compListingQuantityScoreAdjustmentOverrideJson !==
        defaultValues?.compListingQuantityScoreAdjustmentOverrideJson ||
      input.undercutMode !== defaultValues?.undercutMode ||
      input.undercutAbsoluteAmount !== defaultValues?.undercutAbsoluteAmount ||
      input.undercutRelativeAmount !== defaultValues?.undercutRelativeAmount ||
      input.circuitBreakerMaxDiscountVelocityPercent !==
        defaultValues?.circuitBreakerMaxDiscountVelocityPercent ||
      input.circuitBreakerMaxDiscountVelocityTicksInHours !==
        defaultValues?.circuitBreakerMaxDiscountVelocityTicksInHours ||
      input.circuitBreakerMinCompListingCount !==
        defaultValues?.circuitBreakerMinCompListingCount ||
      input.circuitBreakerRelativeCeiling !==
        defaultValues?.circuitBreakerRelativeCeiling ||
      input.circuitBreakerRelativeFloor !==
        defaultValues?.circuitBreakerRelativeFloor ||
      input.outlierMode !== defaultValues?.outlierMode ||
      input.outlierStandardDeviations !==
        defaultValues?.outlierStandardDeviations ||
      input.outlierKthLowestLimit !== defaultValues?.outlierKthLowestLimit ||
      input.outlierKthLowestLimitRelativeSpacing !==
        defaultValues?.outlierKthLowestLimitRelativeSpacing ||
      input.outlierKthLowestLimitAbsoluteSpacing !==
        defaultValues?.outlierKthLowestLimitAbsoluteSpacing ||
      input.skipAdvancePricing !== defaultValues?.skipAdvancePricing ||
      input.skipAutoPricing !== defaultValues?.skipAutoPricing ||
      input.skipCompListing !== defaultValues?.skipCompListing ||
      input.skipUndercut !== defaultValues?.skipUndercut ||
      !isEqual(
        input.compListingSelectedSectionSettings,
        defaultValues?.compListingSelectedSectionSettings
      ) ||
      !isEqual(
        input.compListingQuantityFilters,
        defaultValues?.compListingQuantityFilters
      ) ||
      input.undercutReferenceListingPosition !==
        defaultValues?.undercutReferenceListingPosition
    );
  }, [input, methods.formState.defaultValues]);

  const hasSelectedSections = useMemo(() => {
    return (
      (input.compListingSelectedSectionSettings?.sectionIdFilter?.length ?? 0) >
      0
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(input.compListingSelectedSectionSettings)]);

  return {
    hasChanges,
    hasSelectedSections,
    methods,
    onSubmit,
    onReset,
  };
};
